/**
 * areasModel.js — pure helpers for merging BIDIT canonical areas with
 * tenant-added custom sub-areas and tenant custom_areas overrides, and
 * enforcing governance rules.
 *
 * No React dependency. Driveable via unit tests.
 *
 * Shape contract:
 *
 *   BiditArea = {
 *     value: 'ceilings',       // stable string id (matches vocabSeeds.area.values[i].value)
 *     label: 'Ceilings',
 *     subAreas: [{ value, label }],
 *   }
 *
 *   TenantSubArea = {
 *     id: string,              // uuid from backend OR (PR-67a) a BIDIT seed sub-area's `value`
 *                              // when the row is an upsert-shaped label override for that seed.
 *     name: string,            // display label (input field is 'Sub-area name')
 *     parent_area_id: string,  // must match a BiditArea.value (or a custom_areas.id)
 *     added_by?: string,       // user id (optional; wired for future strict-delete policy)
 *   }
 *
 *   CustomAreaOverride = {
 *     id: string,              // if matches a BiditArea.value → label override for that seed area
 *                              // else → new top-level tenant area with this id
 *     name: string,            // display label
 *     subAreas?: [{ id, name, ... }],  // optional; nested overrides / additions
 *     active?: boolean,
 *   }
 *
 *   MergedArea (output shape) = {
 *     id: 'ceilings',          // renamed from `value` for downstream ergonomics
 *     label: 'Ceilings',
 *     source: 'bidit' | 'tenant',
 *     subAreas: [
 *       { id: string, label: string, source: 'bidit' | 'tenant', addedBy?: string }
 *     ],
 *   }
 *
 * Deterministic ordering:
 *   - Top-level areas: BIDIT seed order, then custom_areas (new tenant areas) in row order.
 *   - Sub-areas: seed sub-areas first (in seed order, with any label overrides applied),
 *     then tenant sub-areas (both from `sub_areas` list and from a matching custom_areas
 *     row's nested `subAreas`) in the order they were added.
 *
 * Defensive drop: tenant sub-areas whose `parent_area_id` doesn't correspond
 * to a BIDIT area id (or a custom_areas id) are silently dropped. Post-PR-B2b-part-3
 * this shouldn't happen (parent is a select scoped to BIDIT ids), but the helper
 * enforces it anyway for legacy data + build-time typos.
 *
 * PR-67a (2026-08-22): three related extensions folded into one function.
 *
 *   (1) Third argument `customAreasOverrides` accepts `settings.custom_areas`.
 *       Two roles per row, keyed on id-vs-seed-id:
 *         a) id matches a seed area's `value` → LABEL OVERRIDE for that seed
 *            area; the row's optional nested `subAreas` are treated as
 *            sub-area overrides (id matches a seed sub-area's `value` →
 *            label override) or additions (unmatched id → new tenant
 *            sub-area).
 *         b) id does not match any seed → new top-level tenant area,
 *            appended after the seed areas in row order.
 *
 *   (2) A `sub_areas` row whose `id` matches a seed sub-area's `value`
 *       (upsert shape emitted by the BIDIT-sub-area rename repair in
 *       QuoteAreasPage) is treated as a LABEL OVERRIDE for that seed
 *       sub-area, NOT as an addition. Without this, the merge would return
 *       two entries with the same id (seed + tenant), which downstream
 *       code (`find`, react `key`) handles inconsistently.
 *
 *   (3) The old two-argument signature still works: `customAreasOverrides`
 *       defaults to `[]`, and rule (2) is a no-op when no `sub_areas` row
 *       collides with a seed id (the pre-PR-67a norm).
 */

/**
 * Merge BIDIT canonical areas with tenant custom sub-areas and custom_areas
 * overrides.
 *
 * @param {Array} biditAreas             - vocabSeeds.area.values shape: [{value, label, subAreas}]
 * @param {Array} tenantSubAreas         - [{id, name, parent_area_id, added_by?}]
 * @param {Array} [customAreasOverrides] - settings.custom_areas rows: [{id, name, subAreas?, active?}]
 * @param {object} [opts]
 * @param {boolean} [opts.includeInactive=false] - IDI-448. Default (Add Item):
 *   inactive areas and sub-areas are NOT returned (IDI-405 D4). With true
 *   (Settings page, and the Edit label lookup in AddItemCascade) they are
 *   returned and stamped `active: false`. Renamed labels apply either way.
 *   Active entries never carry an `active` key, so existing output shapes
 *   are unchanged.
 * @returns {Array<MergedArea>}
 */
export function mergeAreasWithTenant(biditAreas, tenantSubAreas, customAreasOverrides = [], opts = {}) {
  const includeInactive = Boolean(opts && opts.includeInactive);
  const isInactive = (row) => Boolean(row) && row.active === false;
  // Keep a row if it is active, or if the caller asked for inactive rows.
  const offered = (row) => includeInactive || !isInactive(row);
  // Stamp `active: false` on an output entry; active entries stay unstamped.
  const stamp = (entry, inactive) => (inactive ? { ...entry, active: false } : entry);
  const areas = Array.isArray(biditAreas) ? biditAreas : [];
  const tenant = Array.isArray(tenantSubAreas) ? tenantSubAreas : [];
  const overrides = Array.isArray(customAreasOverrides) ? customAreasOverrides : [];

  // Index tenant sub-areas by parent for O(1) lookup during the outer map.
  const tenantByParent = new Map();
  for (const row of tenant) {
    if (!row || typeof row !== 'object') continue;
    const parent = row.parent_area_id;
    if (!parent) continue; // defensive drop: no parent → orphan
    if (!tenantByParent.has(parent)) tenantByParent.set(parent, []);
    tenantByParent.get(parent).push(row);
  }

  // Partition custom_areas rows into (a) label overrides for a seed area
  // and (b) new top-level tenant areas. Keyed on id-vs-seed-id.
  const seedIds = new Set(areas.map((a) => a.value));
  const overrideById = new Map();      // seed-id → override row
  const newTenantAreas = [];           // rows whose id is not a seed id
  // IDI-448: built-in areas the DEALER made inactive from Settings. Only a
  // dealer-written override counts: the page's Make inactive always sends
  // isBiditOverride, and the settings Lambda stamps source:"user" on every
  // add. A bare {id: seed, active:false} keeps IDI-405 D4's rule (it neither
  // renames nor hides the built-in). Hidden by default; with includeInactive
  // the built-in is returned stamped inactive under the dealer's label.
  const inactiveSeedIds = new Set();
  for (const row of overrides) {
    if (!row || typeof row !== 'object' || !row.id) continue;
    const dealerWritten = row.isBiditOverride === true || row.source === 'user';
    if (seedIds.has(row.id) && isInactive(row) && dealerWritten) {
      inactiveSeedIds.add(row.id);
      overrideById.set(row.id, row);
      continue;
    }
    // IDI-405 D4: an inactive custom_areas row is hidden from Add Item: it
    // is neither a label override nor a new top-level area (1st Choice's
    // empty second "Walls", 2266). IDI-448: with includeInactive a tenant
    // area is kept (and stamped inactive) for Settings' show-inactive control.
    if (isInactive(row) && (seedIds.has(row.id) || !includeInactive)) continue;
    if (seedIds.has(row.id)) {
      overrideById.set(row.id, row);
    } else {
      newTenantAreas.push(row);
    }
  }

  const mergedSeedAreas = areas
    .filter((area) => includeInactive || !inactiveSeedIds.has(area.value))
    .map((area) => {
    const override = overrideById.get(area.value) || null;
    // Seed sub-area ids (pre-computed so we can classify flat + nested rows).
    const seedSubIds = new Set((area.subAreas || []).map((s) => s.value));
    // IDI-381: seed sub-area LABELS (lowercased/trimmed), so a migrated row
    // with a different id but the SAME name as a seed sub-area is recognised
    // as a duplicate, not a new addition. Measured live (Sample Dealer,
    // tenant 1): migrated sub_area_template rows under Ceilings (Inaccessible,
    // Coffered, Bay, Cathedral, Vaulted, Cold Floor, GCLA) carry LEGACY
    // NUMERIC ids that never match the seed's string values (inaccessible,
    // coffered, ...) even though the NAMES are identical — so they were
    // appended as "additions" alongside the seed's own same-named sub-areas,
    // rendering each of those 7 twice ("Custom" badge + the seed original).
    const seedSubLabelSet = new Set(
      (area.subAreas || []).map((s) => (s.label || '').trim().toLowerCase())
    );

    // (a) Label overrides for seed sub-areas coming from the custom_areas
    // row's nested `subAreas`.
    const overrideSubById = new Map();
    const overrideAddedSubs = [];
    if (override && Array.isArray(override.subAreas)) {
      for (const sub of override.subAreas) {
        if (!sub || typeof sub !== 'object' || !sub.id) continue;
        if (seedSubIds.has(sub.id)) {
          overrideSubById.set(sub.id, sub);
        } else if (seedSubLabelSet.has((sub.name || '').trim().toLowerCase())) {
          // IDI-381: same name-dedup guard as the flat-rows path below.
          continue;
        } else {
          overrideAddedSubs.push(sub);
        }
      }
    }

    // (b) PR-67a rule (2): flat `sub_areas` rows for THIS area, split into
    // (i) label overrides for seed sub-areas (id matches a seed sub-area's
    // value) and (ii) tenant additions (id does not match).
    const flatRows = (tenantByParent.get(area.value) || []);
    const flatOverrideSubById = new Map();
    const flatAddedSubs = [];
    for (const t of flatRows) {
      if (t && t.id && seedSubIds.has(t.id)) {
        flatOverrideSubById.set(t.id, t);
      } else if (t && seedSubLabelSet.has((t.name || '').trim().toLowerCase())) {
        // IDI-381: same NAME as an existing seed sub-area, different id —
        // this is the same sub-area re-surfacing under its legacy numeric id,
        // not a genuinely new tenant sub-area. Drop it rather than duplicate;
        // the seed's own entry (possibly label-overridden above) already
        // represents it.
        continue;
      } else {
        flatAddedSubs.push(t);
      }
    }

    // IDI-405 D1: a MIGRATED dealer (has sub-areas whose origin is legacy
    // under this area) sees only its own sub-areas. A built-in sub-area is
    // kept, once, only if the dealer renamed it (id override, flat or nested)
    // or a legacy row carries the same name (the IDI-381 name-match above
    // already dropped that row, so the seed entry represents it with its own
    // id and label). Row origin decides, never id shape. No legacy rows ->
    // exactly the pre-IDI-405 output (built-ins plus additions).
    const legacyRows = flatRows.filter((t) => t && t.origin === 'legacy');
    const legacyNames = new Set(
      legacyRows.map((t) => (t.name || '').trim().toLowerCase())
    );
    const keepSeedSub = (s) =>
      legacyRows.length === 0 ||
      flatOverrideSubById.has(s.value) ||
      overrideSubById.has(s.value) ||
      legacyNames.has((s.label || '').trim().toLowerCase());
    // IDI-448: a legacy row name-matched to a seed sub-area (dropped above,
    // the seed entry represents it) carries its inactive flag to the seed
    // entry when EVERY legacy row with that name is inactive.
    const activeLegacyNames = new Set(
      legacyRows.filter((t) => !isInactive(t)).map((t) => (t.name || '').trim().toLowerCase())
    );
    const seedInactive = (s) => {
      const flat = flatOverrideSubById.get(s.value);
      if (flat && typeof flat.active === 'boolean') return flat.active === false;
      const nested = overrideSubById.get(s.value);
      if (nested && typeof nested.active === 'boolean') return nested.active === false;
      const name = (s.label || '').trim().toLowerCase();
      return legacyNames.has(name) && !activeLegacyNames.has(name);
    };

    // Seed sub-areas, with per-id label overrides applied. Priority when a
    // seed id has BOTH a custom_areas.subAreas override AND a flat sub_areas
    // upsert row: the flat row wins (it's the newer write path from the
    // BIDIT-sub-area rename repair).
    const biditSubs = (area.subAreas || [])
      .filter(keepSeedSub)
      .filter((s) => includeInactive || !seedInactive(s))
      .map((s) => {
      const flatOverride = flatOverrideSubById.get(s.value);
      const nestedOverride = overrideSubById.get(s.value);
      const chosenOverride = flatOverride || nestedOverride;
      // Labor fields can be overridden by either the flat upsert row or the
      // nested override row; the flat row wins for the same reason as label.
      return stamp({
        id: s.value,
        label: chosenOverride && chosenOverride.name ? chosenOverride.name : s.label,
        source: 'bidit',
        laborMethod:
          (chosenOverride && chosenOverride.laborMethod) || s.laborMethod || null,
        laborRate:
          chosenOverride && chosenOverride.laborRate !== undefined
            ? chosenOverride.laborRate
            : (s.laborRate ?? null),
        laborUnit:
          (chosenOverride && chosenOverride.laborUnit) || s.laborUnit || null,
        inheritsDefault:
          chosenOverride && chosenOverride.inheritsDefault !== undefined
            ? chosenOverride.inheritsDefault !== false
            : s.inheritsDefault !== false,
      }, seedInactive(s));
    });

    // Tenant sub-areas: flat additions (not label overrides), then override-row
    // additions (rare; supported for round-trip symmetry).
    //
    // active-flag policy (IDI-448, supersedes PR-67a's split policy):
    //   both flat sub_areas rows and custom_areas nested subAreas are
    //   FILTERED on active !== false by default, so Add Item never offers an
    //   inactive sub-area. With includeInactive they are kept and stamped
    //   `active: false` (Settings page, Edit label lookup).
    const flatTenantSubs = flatAddedSubs.filter(offered).map((t) => stamp({
      id: t.id,
      label: t.name,
      source: 'tenant',
      ...(t.added_by ? { addedBy: t.added_by } : {}),
      laborMethod: t.laborMethod || null,
      laborRate: t.laborRate ?? null,
      laborUnit: t.laborUnit || null,
      inheritsDefault: t.inheritsDefault !== false,
    }, isInactive(t)));
    const overrideAddedTenantSubs = overrideAddedSubs
      .filter((t) => t && offered(t))
      .map((t) => stamp({
        id: t.id,
        label: t.name,
        source: 'tenant',
        ...(t.added_by ? { addedBy: t.added_by } : {}),
        laborMethod: t.laborMethod || null,
        laborRate: t.laborRate ?? null,
        laborUnit: t.laborUnit || null,
        inheritsDefault: t.inheritsDefault !== false,
      }, isInactive(t)));

    // IDI-176 E5 producer (2026-09-05): stamp `originalLabel` only when the
    // tenant's custom_areas row has renamed the seed. Consumer at
    // AddItemCascade.js area picker renders the annotation "(renamed from X)"
    // when originalLabel is present and differs from label. Omitting the key
    // when there's no rename is intentional — the picker's conditional
    // (`area.originalLabel && area.originalLabel !== area.label`) is only
    // truthy when this producer wrote it, so we don't ship a phantom
    // annotation on every seeded area.
    //
    // Producer contract (single source of truth for this key on the
    // mergeAreasWithTenant output): originalLabel appears IFF an override
    // row renamed the seed. There is exactly one other originalLabel writer
    // in the codebase (QuoteAreasPage.js:366) and it writes to a different
    // shape entirely (settings-side custom_areas rows, not the wizard's
    // merged output). Do not conflate the two.
    const renamed =
      override && override.name && override.name !== area.label;
    return stamp({
      id: area.value,
      label: (override && override.name) ? override.name : area.label,
      source: 'bidit',
      ...(renamed ? { originalLabel: area.label } : {}),
      subAreas: [...biditSubs, ...flatTenantSubs, ...overrideAddedTenantSubs],
    }, inactiveSeedIds.has(area.value));
  });

  // New top-level tenant areas — custom_areas rows whose id is not a seed id.
  // Filter policy: same as the retired inline concat in QuoteAreasPage.
  //   • nested and flat sub-areas: filtered on active !== false by default,
  //     kept and stamped with includeInactive (IDI-448).
  //   • top-level active on the custom_areas row itself: FILTERED since
  //     IDI-405 D4 unless includeInactive (see the partition loop above).
  const newTenantAreasMerged = newTenantAreas.map((row) => {
    const nestedSubs = Array.isArray(row.subAreas) ? row.subAreas : [];
    const flatSubsForRow = (tenantByParent.get(row.id) || []);
    // IDI-405: dedup — a sub-area may appear in both the nested
    // row.subAreas AND the flat tenantByParent lookup. The two shapes carry
    // DIFFERENT ids for the same sub-area (nested "SUBAREA#4001" vs flat
    // legacy "4001"; walk v3, 1st Choice: Crawl Space / Labor \ Removal / AIP
    // each drawn twice), so match on the bare id OR the name.
    const seenIds = new Set();
    const seenNames = new Set();
    // IDI-419: also treat `sub-4506` / `subarea_4506` and `4506` as one id.
    const bareId = (id) =>
      String(id).replace(/^[A-Z_]+#/, '').replace(/^sub(?:area)?[-_]/i, '');
    const dedup = (arr) => arr.filter((t) => {
      if (!t || !t.id) return true; // keep items without id (shouldn't happen)
      const id = bareId(t.id);
      const name = (t.label || '').trim().toLowerCase();
      if (seenIds.has(id) || (name && seenNames.has(name))) return false;
      seenIds.add(id);
      if (name) seenNames.add(name);
      return true;
    });
    // IDI-448: the nested copy and the flat row with the SAME bare id are one
    // sub-area, and the flat row is where the dealer's Make inactive /
    // Reactivate lands (settings.subAreas, reconciled by the Lambda), so the
    // flat row's state wins. Then active rows dedup first, so an inactive
    // same-NAME duplicate can never shadow the active one; inactive rows that
    // survive follow (only reachable with includeInactive).
    const flatByBareId = new Map();
    for (const t of flatSubsForRow) {
      if (t && t.id) flatByBareId.set(bareId(t.id), t);
    }
    const dealerFlatName = (t) => {
      const flat = t.id ? flatByBareId.get(bareId(t.id)) : null;
      return flat && flat.source === 'user' && flat.name ? flat.name : null;
    };
    const nestedInactive = (t) => {
      const flat = t.id ? flatByBareId.get(bareId(t.id)) : null;
      return flat && typeof flat.active === 'boolean' ? flat.active === false : isInactive(t);
    };
    const mapped = [
      ...nestedSubs
        .filter((t) => t && (includeInactive || !nestedInactive(t)))
        .map((t) => stamp({
          id: t.id,
          // IDI-448: a rename from Settings lands on the flat row (the
          // Slideover's add-fallback at the same id; the Lambda stamps
          // source:"user"), so a dealer-written name wins. A legacy row's
          // drifted name does not (IDI-419).
          label: dealerFlatName(t) || t.name,
          source: 'tenant',
          ...(t.added_by ? { addedBy: t.added_by } : {}),
          laborMethod: t.laborMethod || null,
          laborRate: t.laborRate ?? null,
          laborUnit: t.laborUnit || null,
          inheritsDefault: t.inheritsDefault !== false,
        }, nestedInactive(t))),
      ...flatSubsForRow.filter((t) => t && offered(t)).map((t) => stamp({
        id: t.id,
        label: t.name,
        source: 'tenant',
        ...(t.added_by ? { addedBy: t.added_by } : {}),
        laborMethod: t.laborMethod || null,
        laborRate: t.laborRate ?? null,
        laborUnit: t.laborUnit || null,
        inheritsDefault: t.inheritsDefault !== false,
      }, isInactive(t))),
    ];
    const subAreas = dedup([
      ...mapped.filter((t) => !isInactive(t)),
      ...mapped.filter((t) => isInactive(t)),
    ]);
    return stamp({
      id: row.id,
      label: row.name,
      source: 'tenant',
      subAreas,
    }, isInactive(row));
  });

  return [...mergedSeedAreas, ...newTenantAreasMerged];
}

/**
 * canDeleteSubArea — governance rule enforcer.
 *
 * Loose policy (default): any tenant user can delete any tenant-added sub-area.
 * Strict policy (opt-in via currentUserId + strict=true): only the user who
 * added it can delete it. Not exercised in this PR; wired for future tightening.
 *
 * @param {object} subArea - a MergedArea.subAreas[i]
 * @param {object} [opts]
 * @param {string} [opts.currentUserId]
 * @param {boolean} [opts.strict] - if true, require addedBy === currentUserId
 * @returns {boolean}
 */
export function canDeleteSubArea(subArea, opts = {}) {
  if (!subArea || subArea.source !== 'tenant') return false;
  if (opts.strict) {
    return Boolean(opts.currentUserId && subArea.addedBy === opts.currentUserId);
  }
  return true;
}

/**
 * canDeleteArea — top-level areas are BIDIT-only. Never deletable via UI.
 *
 * @returns {false}
 */
export function canDeleteArea() {
  return false;
}

/**
 * hasLegacySubAreas (IDI-662 D1) — true when the dealer is MIGRATED: at
 * least one sub_areas row came from legacy (origin "legacy", stamped by the
 * settings Lambda). Row origin decides, never id shape (IDI-405 D1).
 */
export function hasLegacySubAreas(tenantSubAreas) {
  return (Array.isArray(tenantSubAreas) ? tenantSubAreas : [])
    .some((row) => row && row.origin === 'legacy');
}

/**
 * relevantBuiltInAreas (IDI-405 guard, shared since IDI-662) — which built-in
 * (seed) areas are fed to mergeAreasWithTenant for this dealer.
 *
 *   - Clean dealer (no legacy sub-areas): every built-in. The merge applies
 *     renames and the dealer's inactive state as usual, so a rename, Make
 *     inactive or added area never hides the OTHER built-ins (IDI-662 D1).
 *   - Migrated dealer with custom_areas rows: only built-ins it uses — a
 *     sub-area parented to it, an active custom_areas row for it, or a
 *     dealer-written inactive override (IDI-448: kept so Settings can
 *     reactivate it and Edit can resolve it; the default merge still hides
 *     it from Add Item).
 *
 * Seed areas are keyed by `value`, not `id`.
 * opts.subAreasLoading: sub_areas not fetched yet -> strict filter.
 */
export function relevantBuiltInAreas(seeds, tenantSubAreas, customAreas, opts = {}) {
  const base = Array.isArray(seeds) ? seeds : [];
  const subs = Array.isArray(tenantSubAreas) ? tenantSubAreas : [];
  const rows = Array.isArray(customAreas) ? customAreas : [];
  if (rows.length === 0) return base;
  // While sub_areas are still loading the dealer's origin is unknown; keep
  // the strict (pre-IDI-662) filter so no transient all-seeds list seeds an
  // Edit draft that the real rows would then reset (IDI-365).
  const subAreasLoading = Boolean(opts && opts.subAreasLoading);
  if (!subAreasLoading && !hasLegacySubAreas(subs)) return base;
  const usedIds = new Set();
  for (const sub of subs) {
    if (!sub) continue;
    const pid = sub.parentAreaId || sub.parent_area_id || sub.areaId || sub.area_id;
    if (pid) usedIds.add(pid);
  }
  for (const ca of rows) {
    if (!ca || !ca.id) continue;
    // IDI-405 D4: an inactive custom_areas row does not keep a built-in alive
    // unless the dealer wrote it (IDI-448).
    if (ca.active !== false || ca.isBiditOverride === true || ca.source === 'user') {
      usedIds.add(ca.id);
    }
  }
  return base.filter((a) => usedIds.has(a.value));
}
